import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';

import { UserContext } from './hooks/UserContext';
import Header from './components/Header';
import Loading from './components/Loading';
import HookEditModal from './components/hooks/HookEditModal';
import CourseApprovalPane from './components/hooks/CourseApprovalPane';
import HookListView from './components/hooks/HookListView';
import useCourseApproval from './components/hooks/useCourseApproval';
import {
    FILTERS, FILTER_OPTIONS, buildCourseRows, filterCourseRows, coverage, nextCourseNeedingApproval,
} from './components/hooks/courseOverview';
import { STATUS_FILTERS, DEFAULT_STATUS_FILTER } from './components/hooks/hookList';
import { listHooks } from './api/hooks';
import fetchAllCourses, { fetchCategories } from './api/courses';
import './components/hooks/hooks.css';

export const VIEWS = { BY_COURSE: 'course', ALL: 'all' };

const courseFromUrl = () => {
    const id = new URLSearchParams(window.location.search).get('course');
    return id ? Number(id) : null;
};

const viewFromUrl = () => (
    new URLSearchParams(window.location.search).get('view') === VIEWS.ALL ? VIEWS.ALL : VIEWS.BY_COURSE
);

const statusFromUrl = () => {
    const status = new URLSearchParams(window.location.search).get('status');
    return Object.values(STATUS_FILTERS).includes(status) ? status : DEFAULT_STATUS_FILTER;
};

// The URL mirrors the page state (?course=, ?view=all, ?status=) so a
// view can be linked; defaults are left out.
const syncUrl = ({ courseId, view, status }) => {
    const url = new URL(window.location.href);
    const set = (key, value, isDefault) => {
        if (value && !isDefault) url.searchParams.set(key, value);
        else url.searchParams.delete(key);
    };
    set('course', courseId, !courseId);
    set('view', view, view === VIEWS.BY_COURSE);
    set('status', status, view === VIEWS.BY_COURSE || status === DEFAULT_STATUS_FILTER);
    window.history.replaceState(null, '', url);
};

// The Hooks page (CONTEXT.md "Hook") in two views. By course: a course
// overview on the left — which courses still need a decision, which have
// no approved hook — and the selected course's approval pane on the
// right. All hooks: one table across courses, filtered by status
// (approved by default), for managing what the app is asking. Decline =
// delete in both.
export const ManageHooks = () => {
    const { user } = useContext(UserContext);
    const token = user ? user.token : '';

    const [hooks, setHooks] = useState([]);
    const [courses, setCourses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState(FILTERS.NEEDS_APPROVAL);
    const [query, setQuery] = useState('');
    const [categoryId, setCategoryId] = useState(null);
    const [doNotMissOnly, setDoNotMissOnly] = useState(false);
    const [selectedId, setSelectedId] = useState(courseFromUrl);
    const [view, setView] = useState(viewFromUrl);
    const [status, setStatus] = useState(statusFromUrl);
    const [editing, setEditing] = useState(null); // {hook, course}

    useEffect(() => { syncUrl({ courseId: selectedId, view, status }); }, [selectedId, view, status]);

    const load = useCallback(async () => {
        const [hookRows, courseRows, categoryRows] = await Promise.all([
            listHooks(token), fetchAllCourses(token), fetchCategories(token),
        ]);
        setHooks(hookRows);
        setCourses(courseRows);
        setCategories(categoryRows);
    }, [token]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                await load();
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load hooks.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [load]);

    const reload = useCallback(async () => {
        try {
            setHooks(await listHooks(token));
        } catch (err) {
            setError(err.message || 'Failed to reload hooks.');
        }
    }, [token]);

    // The deep-linked course stays pinned so the ?course= link from the
    // courses table always lands somewhere, whatever the filter.
    const [pinnedId] = useState(courseFromUrl);
    const rows = useMemo(() => buildCourseRows(courses, hooks, pinnedId), [courses, hooks, pinnedId]);
    const visibleRows = useMemo(
        () => filterCourseRows(rows, { filter, query, categoryId, doNotMissOnly, pinnedId }),
        [rows, filter, query, categoryId, doNotMissOnly, pinnedId],
    );
    const categoryOptions = useMemo(
        () => categories.map(c => ({ label: c.name, value: c.id })),
        [categories],
    );
    const cover = useMemo(() => coverage(rows), [rows]);

    const selected = rows.find(r => r.id === selectedId) || null;
    const courseHooks = useMemo(
        () => hooks.filter(h => h.course_id === selectedId).sort((a, b) => a.position - b.position),
        [hooks, selectedId],
    );
    const nextCourse = selected ? nextCourseNeedingApproval(rows, selected.id) : null;

    const select = useCallback((id) => {
        setSelectedId(id);
        setError('');
    }, []);

    // From the all-hooks list: open that course in the by-course view.
    const selectFromList = useCallback((id) => {
        select(id);
        setView(VIEWS.BY_COURSE);
    }, [select]);

    const switchView = (next) => {
        setView(next);
        setError('');
    };

    const approval = useCourseApproval({
        token, courseId: selectedId, hooks, setHooks, reload, setError,
    });

    const openEdit = (hook) => setEditing({
        hook, course: { id: hook.course_id, name: hook.course_name || selected?.name || '' },
    });
    const openNew = () => selected && setEditing({ hook: null, course: { id: selected.id, name: selected.name } });
    const onModalClose = ({ saved, hook }) => {
        setEditing(null);
        if (!saved || !hook) return;
        setHooks(prev => (prev.some(h => h.id === hook.id)
            ? prev.map(h => (h.id === hook.id ? hook : h))
            : [...prev, hook]));
    };

    if (loading) return (<div><Header /><Loading /></div>);

    return (
        <div className="App">
            <Header />
            <div className="container-fluid">
                <div className="row mt-4">
                    <div className="col-12 text-center">
                        <h2>Hooks</h2>
                        <p className="hk-page-intro">
                            Guessable questions the app asks as its question of the day. Pick a course,
                            approve the hooks worth asking, delete the rest. Only approved hooks reach readers.
                        </p>
                        <div className="hk-view-toggle mt-3" role="tablist">
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === VIEWS.BY_COURSE}
                                className={view === VIEWS.BY_COURSE ? 'selected' : ''}
                                onClick={() => switchView(VIEWS.BY_COURSE)}
                            >
                                By course
                            </button>
                            <button
                                type="button"
                                role="tab"
                                aria-selected={view === VIEWS.ALL}
                                className={view === VIEWS.ALL ? 'selected' : ''}
                                onClick={() => switchView(VIEWS.ALL)}
                            >
                                All hooks
                            </button>
                        </div>
                    </div>
                </div>
                {view === VIEWS.ALL && (
                    <div className="row mt-3">
                        <div className="col-12">
                            {error && <div className="hk-error hk-section-error">{error}</div>}
                            <HookListView
                                hooks={hooks}
                                courses={courses}
                                categories={categories}
                                status={status}
                                onStatusChange={setStatus}
                                busy={!!approval.busy}
                                onToggleApproval={approval.toggleApproval}
                                onEdit={openEdit}
                                onDelete={approval.remove}
                                onSelectCourse={selectFromList}
                            />
                        </div>
                    </div>
                )}
                {view === VIEWS.BY_COURSE && (
                <div className="row mt-3">
                    <div className="col-12">
                        <div className="hk-layout">
                            <aside className="hk-pane-list">
                                <div className="hk-list-head">
                                    <div className="hk-list-coverage">
                                        {cover.covered} of {cover.total} published courses have an approved hook
                                    </div>
                                    <Dropdown value={filter} options={FILTER_OPTIONS} onChange={(e) => setFilter(e.value)} />
                                    <Dropdown
                                        value={categoryId}
                                        options={categoryOptions}
                                        onChange={(e) => setCategoryId(e.value ?? null)}
                                        placeholder="All categories"
                                        filter
                                        showClear
                                    />
                                    <label className="hk-list-check">
                                        <input
                                            type="checkbox"
                                            checked={doNotMissOnly}
                                            onChange={(e) => setDoNotMissOnly(e.target.checked)}
                                        />
                                        <i className="pi pi-star-fill hk-star" /> Do not miss only
                                    </label>
                                    <span className="p-input-icon-left">
                                        <i className="pi pi-search" />
                                        <InputText
                                            type="search"
                                            value={query}
                                            onChange={(e) => setQuery(e.target.value)}
                                            placeholder="Search courses"
                                        />
                                    </span>
                                </div>
                                <ul className="hk-list">
                                    {visibleRows.length === 0 && (
                                        <li className="hk-empty" style={{ padding: '12px' }}>No courses match.</li>
                                    )}
                                    {visibleRows.map(row => (
                                        <li key={row.id}>
                                            <button
                                                type="button"
                                                className={`hk-list-row${row.id === selectedId ? ' selected' : ''}`}
                                                onClick={() => select(row.id)}
                                                title={row.needsApproval ? 'Has drafts to decide' : row.noApproved ? 'No approved hook' : 'Covered'}
                                            >
                                                <span className={`hk-dot${row.needsApproval ? ' hk-dot-needs' : row.noApproved ? '' : ' hk-dot-covered'}`} />
                                                <span className="hk-list-row-name">
                                                    {row.doNotMiss && <i className="pi pi-star-fill hk-star" title="Do not miss" />}
                                                    {row.name}
                                                    {row.status !== 'published' && <span className="hk-sub"> {row.status}</span>}
                                                </span>
                                                <span className="hk-list-row-counts">
                                                    {row.approved} ✓{row.draft > 0 ? ` · ${row.draft} draft` : ''}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </aside>
                            <main className="hk-pane-detail">
                                {selected ? (
                                    <CourseApprovalPane
                                        course={selected}
                                        hooks={courseHooks}
                                        busy={approval.busy}
                                        error={error}
                                        onToggleApproval={approval.toggleApproval}
                                        onEdit={openEdit}
                                        onDelete={approval.remove}
                                        onAdd={openNew}
                                        onGenerateMore={approval.generateMore}
                                        onDeleteRemainingDrafts={approval.deleteRemainingDrafts}
                                        onReorderApproved={approval.reorderApproved}
                                        nextCourse={nextCourse}
                                        onNextCourse={(course) => select(course.id)}
                                    />
                                ) : (
                                    <div className="hk-pane-placeholder">
                                        {error || 'Pick a course on the left to see its hooks.'}
                                    </div>
                                )}
                            </main>
                        </div>
                    </div>
                </div>
                )}
            </div>
            <HookEditModal
                visible={!!editing}
                hook={editing?.hook || null}
                course={editing?.course || null}
                token={token}
                onClose={onModalClose}
            />
        </div>
    );
};

export default ManageHooks;
